import type { ReactNode } from "react";
import type { ButtonProps, IconProps } from "@/components/types";

export interface ButtonsProps
  extends IconProps,
    Omit<ButtonProps, "action" | "name"> {
  action?: ButtonProps["action"];
  loading?: boolean;
}

/**
 * Props for the ButtonIcon component.
 *
 * @interface ButtonIconProps
 * @extends {Omit<ButtonProps, 'children'>}
 *
 * @description Defines the properties for a button that can include icons.
 * It inherits from `ButtonProps` but redefines `children` and adds icon-specific properties.
 */
export type ButtonIconProps = Omit<ButtonProps, "children"> & {
  /**
   * The content of the button. Can be a string or any React node.
   * @type {string | ReactNode}
   */
  children?: string | ReactNode;

  /**
   * If true, the button will be in a loading state, and a spinner will be displayed.
   * @type {boolean}
   * @default false
   */
  loading?: boolean;

  /**
   * The icon to display on the left side of the button content.
   * @type {ButtonsProps}
   */
  iconLeft?: ButtonsProps;

  /**
   * The icon to display on the right side of the button content.
   * @type {ButtonsProps}
   */
  iconRight?: ButtonsProps;
};
export interface ButtonAvatarProps extends Omit<ButtonProps, "children"> {
  /**
   * The source URL of the avatar image.
   * @type {string}
   */
  src: string;

  /**
   * The alt text for the avatar image.
   * @type {string}
   * @default "User Avatar"
   */
  alt?: string;
}

/**
 * The name of the FontAwesome spinner icon used for the loading state.
 * @type {string}
 */
export const faIconSpinner: string = "spinner:fontawesome:solid";

/**
 * Interface defining the props for the ButtonChangeLayout component.
 */
export interface ButtonChangeLayoutProps {
  /**
   * Callback function executed when the button is clicked.
   * This is typically used to toggle the sidebar's visibility.
   */
  onClick: () => void;

  /**
   * Indicates whether the sidebar is currently open.
   * Used for accessibility attributes (aria-expanded) and label text.
   */
  sidebarIsOpen: boolean;
}
